import React, { useMemo } from 'react';
import { getLocale, useIntl } from 'umi';
import { getEventName } from '@op/pages/Overview/KeyMetrics/utils';
import { listTimeTitleFunc } from '@op/components/ChartCommon/formatter';
import type { ParamsTimeType } from '@op/types/common';
import type { IEventItem } from '@op/components/ChartsEvents/types';
import { getOpinionLineChartEventTypeStyle } from '../utils/eventTypeStyle';
import { getOpinionEventCategoryMeta, getOpinionEventTypeDisplayName } from '../hooks/utils/getOpinionEventCategoryLabel';
import { goOpinionEventDetails } from '../utils/goOpinionEventDetails';
import { OPINION_BAR_AND_LINE_CHART_ROOT_CLASS } from '../constants';
import './index.scss';

export interface IOpinionEventFloaterDetail {
  count?: number;
  event_list?: Record<string, IEventItem[]>;
}

interface IOpinionEventFloaterGroup {
  name: string;
  name_cn?: string;
  elements: IEventItem[];
}

function buildGroupedList(
  detail?: IOpinionEventFloaterDetail | null,
): { count: number; groupedList: IOpinionEventFloaterGroup[] } {
  const event_list = detail?.event_list || {};
  const groupedList: IOpinionEventFloaterGroup[] = [];
  let totalCount = 0;

  Object.keys(event_list).forEach(key => {
    const elements = event_list[key] || [];
    if (!elements.length) return;
    totalCount += elements.length;
    groupedList.push({
      ...getOpinionEventCategoryMeta(key, elements),
      elements,
    });
  });

  return {
    count: detail?.count ?? totalCount,
    groupedList,
  };
}

interface IOpinionEventFloaterProps {
  time: string;
  eventDetail?: IOpinionEventFloaterDetail | null;
  /** More 跳转时使用；未传则与 eventDetail 一致 */
  navigationEventDetail?: IOpinionEventFloaterDetail | null;
  dateType: ParamsTimeType;
  className?: string;
  style?: React.CSSProperties;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export default function OpinionEventFloater({
  time,
  eventDetail,
  navigationEventDetail,
  dateType,
  className = '',
  style,
  onMouseEnter,
  onMouseLeave,
}: IOpinionEventFloaterProps) {
  const { formatMessage } = useIntl();
  const lang = getLocale();

  const detailForDisplay = navigationEventDetail ?? eventDetail;

  const { count, groupedList } = useMemo(
    () => buildGroupedList(detailForDisplay),
    [detailForDisplay],
  );

  const navigationGroupedList = useMemo(() => {
    const detailForNav = navigationEventDetail ?? eventDetail;
    return buildGroupedList(detailForNav).groupedList;
  }, [navigationEventDetail, eventDetail]);

  if (!detailForDisplay || !groupedList.length) {
    return null;
  }

  const handleGoToEventDetails = (): void => {
    if (!navigationGroupedList.length) {
      return;
    }

    goOpinionEventDetails({
      list: navigationGroupedList,
      time: listTimeTitleFunc(time, dateType, true),
    });
  };

  return (
    <div
      className={`${OPINION_BAR_AND_LINE_CHART_ROOT_CLASS} dbi-opinion-event-floater ${className}`.trim()}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="dbi-opinion-event-floater__hd">
        <div className="dbi-opinion-event-floater__hd-row">
          <div className="dbi-opinion-event-floater__hd-left">
            <span className="dbi-opinion-event-floater__hd-label">
              {formatMessage({ id: 'Events-1' })}
            </span>
            <span className="dbi-opinion-event-floater__hd-count">
              ({count} {formatMessage({ id: 'events-1' })})
            </span>
            <span className="dbi-opinion-event-floater__hd-date">
              {listTimeTitleFunc(time, dateType, true)}
            </span>
          </div>
          <button
            type="button"
            className="dbi-opinion-event-floater__hd-more"
            onClick={handleGoToEventDetails}
          >
            {formatMessage({ id: 'More' })}
          </button>
        </div>
      </div>

      <div className="dbi-opinion-event-floater__list">
        {groupedList.map(group => {
          const typeStyle = getOpinionLineChartEventTypeStyle(group.name);

          return (
            <div className="dbi-opinion-event-floater__item" key={group.name}>
              <span
                className="dbi-opinion-event-floater__tag"
                style={{
                  color: typeStyle.textColor,
                  backgroundColor: typeStyle.backgroundColor,
                }}
              >
                {getOpinionEventTypeDisplayName(
                  group.name,
                  lang,
                  group.name_cn,
                )}
              </span>
              <div className="dbi-opinion-event-floater__titles">
                {group.elements.map((event, index) => {
                  const titleKey = `${event.name || 'event'}-${
                    event.start_time || index
                  }`;

                  return (
                    <div
                      className="dbi-opinion-event-floater__title"
                      key={titleKey}
                    >
                      {getEventName(event, lang)}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
